// src/context/AuthContext.js - VERSIÓN CORREGIDA COMPLETA CON UPDATE USER
import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';
import authService from '../services/authService';
import api from '../services/api';
import { unregisterCurrentPushDevice } from '../services/nativePushNotificationService';
import { pendingQuickExpensePath } from '../services/nativeExpenseService';
import { canResumeOfflineSession } from '../utils/offlineSession';

const AuthContext = createContext(null);
const ROLE_HIERARCHY = {
    admin: {
        level: 4,
        label: 'Administrador',
        permissions: ['all', 'manage_users', 'manage_roles']
    },
    manager: {
        level: 3,
        label: 'Gerente',
        permissions: ['view_all', 'edit_own', 'view_reports']
    },
    user: {
        level: 2,
        label: 'Usuario',
        permissions: ['view_own', 'edit_own']
    },
    client: {
        level: 1,
        label: 'Cliente',
        permissions: ['view_own']
    },
    viewer: {
        level: 1,
        label: 'Visualizador',
        permissions: ['view_own']
    }
};

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState([]);
    const [token, setToken] = useState(null);
    const navigate = useNavigate();

    // Verificar sesión al cargar
    useEffect(() => {
        const checkSession = async () => {
            try {
                const currentToken = authService.getToken();
                const savedUser = authService.getUser();

                if (currentToken && savedUser) {
                    setToken(currentToken);
                    
                    try {
                        const currentUser = await authService.getCurrentUser();
                        setUser(currentUser);
                        setPermissions(ROLE_HIERARCHY[currentUser.role]?.permissions || []);
                    } catch (error) {
                        if (canResumeOfflineSession(currentToken, savedUser, error)) {
                            setUser(savedUser);
                            setPermissions(ROLE_HIERARCHY[savedUser.role]?.permissions || []);
                            return;
                        }
                        console.error('Invalid token:', error);
                        authService.logout();
                        setUser(null);
                        setPermissions([]);
                        setToken(null);
                    }
                } else {
                    setUser(null);
                    setPermissions([]);
                    setToken(null);
                }
            } catch (error) {
                console.error('Error checking session:', error);
                setUser(null);
                setPermissions([]);
                setToken(null);
            } finally {
                setLoading(false);
            }
        };

        checkSession();
    }, []);

    const completeAuthentication = (data, { navigateAfter = true } = {}) => {
        if (!data?.token || !data?.user) return { success: false, message: 'La sesión recibida no es válida.' };
        authService.saveSession(data);
        setUser(data.user);
        setToken(data.token);
        setPermissions(ROLE_HIERARCHY[data.user.role]?.permissions || []);
        if (navigateAfter) navigate(pendingQuickExpensePath());
        return { success: true };
    };

    // Función de login
    const login = async (email, password) => {
        try {
            const data = await authService.login(email, password);
            
            if (data.success && data.user) {
                completeAuthentication(data);
                return { success: true };
            } else if (data.success && (data.mfa_required || data.mfa_enrollment_required)) {
                return data;
            } else {
                return { 
                    success: false, 
                    message: data.message || 'Unable to sign in' 
                };
            }
        } catch (error) {
            console.error('Error en login:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Error al conectar con el servidor' 
            };
        }
    };

    // Función de logout
    const logout = async () => {
        try {
            await unregisterCurrentPushDevice(token).catch((error) => {
                console.warn('No fue posible desactivar el token push al cerrar sesión:', error);
            });
            await authService.logout();
        } catch (error) {
            console.error('Error en logout:', error);
        } finally {
            setUser(null);
            setPermissions([]);
            setToken(null);
            navigate('/login');
        }
    };

    // Función de registro
    const register = async (userData) => {
        try {
            const data = await authService.register(userData);
            if (data.success && data.user) {
                return { success: true, user: data.user };
            } else {
                return { 
                    success: false, 
                    message: data.message || 'Error al registrar usuario' 
                };
            }
        } catch (error) {
            console.error('Error en register:', error);
            return { 
                success: false, 
                message: error.response?.data?.message || 'Error al conectar con el servidor' 
            };
        }
    };

    // ✅ NUEVA FUNCIÓN: Actualizar usuario en caliente (Sin logout)
    // Esto es lo que faltaba para que la foto se actualice sola
    const updateUser = (userData) => {
        // Actualizamos el estado de React
        setUser(userData);
        
        // Actualizamos la persistencia (localStorage) a través del servicio
        // Asumiendo que authService tiene un método para guardar user, o lo hacemos manual:
        localStorage.setItem('user', JSON.stringify(userData));
    };

    // Verificar si tiene un permiso específico
    const hasPermission = (permission) => {
        if (!user) return false;
        if (user.role === 'admin') return true;
        return permissions.includes(permission) || permissions.includes('all');
    };

    // Verificar si tiene un rol específico
    const hasRole = (roleName) => {
        return user?.role === roleName;
    };

    const hasAllPermissions = (permissionList) => {
        return permissionList.every(perm => hasPermission(perm));
    };

    const hasAnyPermission = (permissionList) => {
        return permissionList.some(perm => hasPermission(perm));
    };

    const updateUserRole = async (userId, newRole) => {
        if (!hasPermission('manage_users')) {
            throw new Error('No tienes permiso para cambiar roles');
        }

        try {
            const { data } = await api.put(`/users/${userId}/role`, { role: newRole });
            return data;
        } catch (error) {
            console.error('Error al actualizar rol:', error);
            throw error;
        }
    };

    const verifyMfaLogin = async (mfaToken, code) => {
        try {
            const data = await authService.verifyMfaLogin(mfaToken, code);
            if (data.success && data.user) {
                completeAuthentication(data);
                return { success: true, recovery_code_used: data.recovery_code_used };
            }
            return { success: false, message: data.message || 'No fue posible validar el código.' };
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'Error al validar el código.' };
        }
    };

    const startMfaSetup = async (mfaToken, password) => {
        try {
            return await authService.startMfaSetup(mfaToken, password);
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'No fue posible iniciar la configuración.' };
        }
    };

    const verifyMfaSetup = async (mfaToken, code) => {
        try {
            return await authService.verifyMfaSetup(mfaToken, code);
        } catch (error) {
            return { success: false, message: error.response?.data?.message || 'No fue posible confirmar la configuración.' };
        }
    };

    const value = { 
        user, 
        token,
        loading, 
        login,
        completeAuthentication,
        verifyMfaLogin,
        startMfaSetup,
        verifyMfaSetup,
        logout, 
        register,
        updateUser, // ✅ EXPORTADO
        isAuthenticated: !!user,
        permissions,
        hasPermission,
        hasRole,
        hasAllPermissions,
        hasAnyPermission,
        updateUserRole,
        ROLE_HIERARCHY
    };

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }
    return context;
}

export { AuthContext };
